<template>
  <div class="member-csv-upload-component">
    <div class="csv-header">
      <div>
        <label class="form-label">上傳 CSV 名單</label>
        <p class="csv-desc">支援：手機號碼（09xxxxxxxx）、Email、LINE User ID，每行一筆，最多 10000 筆</p>
      </div>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        @click="downloadSample"
      >
        <i class="ri-download-line"></i>
        下載範例 CSV
      </button>
    </div>

    <div
      class="csv-dropzone"
      :class="{ 'csv-dropzone--active': dragOver, 'csv-dropzone--done': !!csvFile }"
      @dragover.prevent="dragOver = true"
      @dragleave="dragOver = false"
      @drop.prevent="handleDrop"
      @click="csvInputRef?.click()"
    >
      <input
        ref="csvInputRef"
        type="file"
        accept=".csv,text/csv"
        style="display:none"
        @change="handleFileChange"
      />
      <template v-if="!csvFile">
        <i class="ri-upload-cloud-2-line csv-dropzone-icon"></i>
        <p class="csv-dropzone-text">點擊或拖曳 CSV 檔案到此處</p>
        <p class="csv-dropzone-hint">檔案格式：.csv，每行一個識別資訊</p>
      </template>
      <template v-else>
        <i class="ri-file-text-line csv-dropzone-icon csv-dropzone-icon--done"></i>
        <p class="csv-dropzone-text">{{ csvFile.name }}</p>
        <p class="csv-dropzone-hint">已解析 {{ parsedCount }} 筆識別資訊</p>
        <button
          type="button"
          class="btn btn-sm btn-link csv-reselect"
          @click.stop="clearFile"
        >
          <i class="ri-close-line"></i> 重新選擇
        </button>
      </template>
    </div>

    <div v-if="uploadStatus === 'uploading'" class="csv-status csv-status--uploading">
      <i class="ri-loader-4-line spin"></i> 備份至雲端中...
    </div>
    <div v-if="uploadStatus === 'done'" class="csv-status csv-status--done">
      <i class="ri-check-line"></i> 檔案已備份至雲端
    </div>
    <div v-if="uploadStatus === 'error'" class="csv-status csv-status--error">
      <i class="ri-error-warning-line"></i> 備份失敗，紀錄將不含來源檔連結
    </div>

    <div class="form-group" style="margin-top: 12px;">
      <button
        type="button"
        class="btn btn-primary"
        @click="searchMembers"
        :disabled="searching || !csvFile || disabled"
      >
        <span v-if="searching">
          <i class="ri-loader-4-line spin"></i>
          搜尋中...
        </span>
        <span v-else>
          <i class="ri-search-line"></i>
          搜尋會員
        </span>
      </button>
    </div>

    <div v-if="results.length > 0" class="member-results">
      <div class="results-header">
        <span>
          找到 {{ results.length }} 位會員
          <span v-if="results.length > DISPLAY_LIMIT" class="results-overflow-hint">
            （僅顯示前 {{ DISPLAY_LIMIT }} 筆）
          </span>
        </span>
        <button
          type="button"
          class="btn btn-sm btn-link"
          @click="addAll"
          :disabled="disabled"
        >
          <i class="ri-add-line"></i>
          全部加入（{{ results.length }} 位）
        </button>
      </div>
      <div class="member-list">
        <div
          v-for="member in displayResults"
          :key="member.id"
          class="member-item"
          :class="{ selected: isSelected(member.id) }"
          @click="toggle(member)"
        >
          <div class="member-info">
            <div class="member-name">{{ member.name }}</div>
            <div class="member-details">
              <span v-if="member.phone">{{ member.phone }}</span>
              <span v-if="member.email">{{ member.email }}</span>
            </div>
          </div>
          <div v-if="isSelected(member.id)" class="check-icon">
            <i class="ri-check-line"></i>
          </div>
        </div>
      </div>
      <div v-if="results.length > DISPLAY_LIMIT" class="results-overflow-note">
        <i class="ri-information-line"></i>
        列表僅顯示前 {{ DISPLAY_LIMIT }} 筆，點「全部加入」可加入全部 {{ results.length }} 位會員
      </div>
    </div>

    <div v-if="searched && results.length === 0" class="no-results">
      <i class="ri-user-search-line"></i>
      <p>找不到符合的會員</p>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { memberService } from '@/api/modules/members.js';
import { showError } from '@/utils/swal.js';

export default {
  name: 'MemberCsvUpload',
  props: {
    modelValue: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit, expose }) {
    const DISPLAY_LIMIT = 100;

    const csvFile = ref(null);
    const csvInputRef = ref(null);
    const dragOver = ref(false);
    const parsedCount = ref(0);
    const parsedIdentifiers = ref([]);
    const uploadStatus = ref('');
    const results = ref([]);
    const searching = ref(false);
    const searched = ref(false);

    const displayResults = computed(() => results.value.slice(0, DISPLAY_LIMIT));

    const isSelected = (memberId) => props.modelValue.some(m => m.id === memberId);

    const toggle = (member) => {
      const current = [...props.modelValue];
      const index = current.findIndex(m => m.id === member.id);
      if (index > -1) {
        current.splice(index, 1);
      } else {
        current.push(member);
      }
      emit('update:modelValue', current);
    };

    const addAll = () => {
      const current = [...props.modelValue];
      results.value.forEach(member => {
        if (!current.some(m => m.id === member.id)) current.push(member);
      });
      emit('update:modelValue', current);
    };

    const parseCsvText = (text) =>
      text
        .split(/[\r\n]+/)
        .map(line => line.trim())
        .filter(line => line.length > 0 && line !== '識別資訊' && !line.startsWith('#'));

    const downloadSample = () => {
      const sample = ['識別資訊', '0901234567', '0912345678', 'user@example.com', 'U4c2d3eefd46ae59b4b30e0c5e96259ef'].join('\n');
      const blob = new Blob(['\uFEFF' + sample], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = '會員名單範例.csv';
      a.click();
      URL.revokeObjectURL(url);
    };

    const processFile = async (file) => {
      csvFile.value = file;
      uploadStatus.value = '';
      searched.value = false;
      results.value = [];
      const text = await file.text();
      const identifiers = parseCsvText(text);
      parsedIdentifiers.value = identifiers;
      parsedCount.value = identifiers.length;
    };

    const handleFileChange = (event) => {
      const file = event.target.files[0];
      if (file) processFile(file);
    };

    const handleDrop = (event) => {
      dragOver.value = false;
      const file = event.dataTransfer.files[0];
      if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
        processFile(file);
      } else {
        showError('請上傳 CSV 格式的檔案');
      }
    };

    const clearFile = () => {
      csvFile.value = null;
      parsedCount.value = 0;
      parsedIdentifiers.value = [];
      uploadStatus.value = '';
      searched.value = false;
      results.value = [];
      if (csvInputRef.value) csvInputRef.value.value = '';
    };

    const searchMembers = async () => {
      if (!csvFile.value) { showError('請先上傳 CSV 檔案'); return; }
      const lines = parsedIdentifiers.value;
      if (lines.length === 0) { showError('CSV 內容為空，請確認格式'); return; }
      if (lines.length > 10000) { showError('最多只能輸入 10000 筆識別資訊'); return; }

      searching.value = true;
      results.value = [];
      searched.value = true;
      try {
        const response = await memberService.getMembersByIdentifiers(lines);
        if (response?.success && response.data) {
          results.value = response.data || [];
        } else {
          showError(response?.message || '搜尋失敗');
        }
      } catch (err) {
        console.error('根據識別資訊搜尋會員失敗:', err);
        showError('搜尋失敗');
      } finally {
        searching.value = false;
      }
    };

    const setCsvUploadStatus = (status) => {
      uploadStatus.value = status;
    };

    const reset = () => {
      clearFile();
    };

    expose({ csvFile, setCsvUploadStatus, reset });

    return {
      DISPLAY_LIMIT,
      csvFile,
      csvInputRef,
      dragOver,
      parsedCount,
      uploadStatus,
      results,
      displayResults,
      searching,
      searched,
      isSelected,
      toggle,
      addAll,
      downloadSample,
      handleFileChange,
      handleDrop,
      clearFile,
      searchMembers
    };
  }
};
</script>

<style scoped>
.csv-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
}

.csv-desc {
  font-size: 13px;
  color: #6c757d;
  margin: 4px 0 0;
}

.csv-dropzone {
  border: 2px dashed #d0d7de;
  border-radius: 8px;
  padding: 32px 24px;
  text-align: center;
  cursor: pointer;
  background: #fafbfc;
  position: relative;
  transition: all 0.2s;
}

.csv-dropzone:hover,
.csv-dropzone--active {
  border-color: #0d6efd;
  background: #f0f5ff;
}

.csv-dropzone--done {
  border-color: #198754;
  background: #f0fff4;
  cursor: default;
}

.csv-dropzone-icon {
  font-size: 40px;
  color: #6c757d;
  display: block;
  margin-bottom: 8px;
}

.csv-dropzone-icon--done { color: #198754; }

.csv-dropzone-text {
  font-size: 15px;
  font-weight: 500;
  color: #333;
  margin: 0 0 4px;
}

.csv-dropzone-hint {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

.csv-reselect {
  margin-top: 8px;
  font-size: 13px;
}

.csv-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  padding: 6px 0;
}

.csv-status--uploading { color: #0d6efd; }
.csv-status--done { color: #198754; }
.csv-status--error { color: #dc3545; }

.member-results {
  margin-top: 16px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  font-size: 14px;
  color: #666;
  border-bottom: 1px solid #e9ecef;
}

.results-overflow-hint {
  color: #6c757d;
  font-size: 13px;
}

.member-list {
  max-height: 300px;
  overflow-y: auto;
}

.member-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.member-item:hover { background: #f8f9fa; }
.member-item.selected { background: #e7f3ff; border-color: #b3d9ff; }
.member-item:last-child { border-bottom: none; }

.member-info { flex: 1; }

.member-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.member-details {
  font-size: 13px;
  color: #666;
  display: flex;
  gap: 12px;
}

.check-icon {
  color: #28a745;
  font-size: 20px;
}

.results-overflow-note {
  padding: 8px 12px;
  font-size: 13px;
  color: #6c757d;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.no-results {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.no-results i {
  font-size: 48px;
  margin-bottom: 12px;
  display: block;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}
</style>
